import { useEffect, useMemo, useState } from "react";
import { Tag as TagIcon } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { createTag, pickRandomTagColor } from "../../lib/digitalTwinsApi";
import api from "../../lib/api";

interface AssetOption {
  id: string;
  name: string;
  asset_code: string;
}

interface PlaceTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  digitalTwinId: string;
  virtualSiteId: string;
  worldPoint: { x: number; y: number; z: number } | null;
  onCreated: () => void;
  /** Asset IDs already tagged on this twin — hidden from the dropdown so the
   *  user can't trigger a 409 from the uniqueness rule. */
  taggedAssetIds?: ReadonlyArray<string>;
  /** Colors of existing tags on this twin — used to bias the random default
   *  color toward an unused one. */
  usedColors?: ReadonlyArray<string | null | undefined>;
}

export function PlaceTagModal({
  isOpen, onClose, digitalTwinId, virtualSiteId, worldPoint, onCreated,
  taggedAssetIds = [], usedColors = [],
}: PlaceTagModalProps) {
  const [assets, setAssets] = useState<AssetOption[]>([]);
  const [assetId, setAssetId] = useState("");
  const [label, setLabel] = useState("");
  // Auto-pick a random color when the modal opens so successive tags don't
  // all stack up in the default indigo. The user can still override via the
  // color picker.
  const [color, setColor] = useState<string>(() => pickRandomTagColor(usedColors));
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    // Each time the modal opens, refresh the random color against the
    // current usedColors so we keep biasing away from already-placed tags.
    setColor(pickRandomTagColor(usedColors));
    let cancelled = false;
    setIsLoadingAssets(true);
    api
      .get("/assets", { params: { virtual_site_id: virtualSiteId, page_size: 100 } })
      .then((res) => {
        if (!cancelled) {
          setAssets(res.data.items || []);
        }
      })
      .catch(() => {
        if (!cancelled) setAssets([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingAssets(false);
      });
    return () => {
      cancelled = true;
    };
    // usedColors intentionally excluded — only re-randomize on modal open,
    // not on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, virtualSiteId]);

  // Available assets = all in site, minus those already tagged on this twin.
  // Backend enforces the same rule and would 409, but pre-filtering keeps
  // the dropdown clean.
  const availableAssets = useMemo(() => {
    const taken = new Set(taggedAssetIds);
    return assets.filter((a) => !taken.has(a.id));
  }, [assets, taggedAssetIds]);

  function reset() {
    setAssetId("");
    setLabel("");
    setColor(pickRandomTagColor(usedColors));
    setError(null);
  }

  function handleClose() {
    if (isCreating) return;
    reset();
    onClose();
  }

  async function handleCreate() {
    if (!worldPoint || !assetId) {
      setError("Pick an asset to link this tag to.");
      return;
    }
    setIsCreating(true);
    setError(null);
    try {
      await createTag({
        asset_id: assetId,
        digital_twin_id: digitalTwinId,
        tag_type: "point",
        geometry: { x: worldPoint.x, y: worldPoint.y, z: worldPoint.z },
        label: label || undefined,
        color,
      });
      onCreated();
      reset();
      onClose();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail || "Failed to create tag");
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Place Asset Tag" size="md">
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {worldPoint && (
          <div className="rounded-lg border border-surface-700 bg-surface-900/50 px-4 py-3 text-xs text-surface-400">
            <div className="flex items-center gap-2">
              <TagIcon className="h-3.5 w-3.5 text-brand-400" />
              <span className="font-medium text-surface-200">3D Position</span>
            </div>
            <div className="mt-1 font-mono">
              x: {worldPoint.x.toFixed(3)} · y: {worldPoint.y.toFixed(3)} · z: {worldPoint.z.toFixed(3)}
            </div>
          </div>
        )}

        <div>
          <label className="mb-1.5 block text-sm font-medium text-surface-200">
            Asset <span className="text-red-400">*</span>
          </label>
          <select
            value={assetId}
            onChange={(e) => setAssetId(e.target.value)}
            disabled={isLoadingAssets || isCreating}
            className="w-full rounded-lg border border-surface-700 bg-surface-900 px-3 py-2 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">
              {isLoadingAssets
                ? "Loading assets…"
                : availableAssets.length === 0
                  ? "All assets in this site are already tagged"
                  : "Select an asset"}
            </option>
            {availableAssets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.asset_code} — {a.name}
              </option>
            ))}
          </select>
          {taggedAssetIds.length > 0 && (
            <p className="mt-1 text-[11px] text-surface-500">
              {taggedAssetIds.length} asset
              {taggedAssetIds.length === 1 ? "" : "s"} already tagged — hidden from the list above. Each asset can carry only one 3D tag.
            </p>
          )}
        </div>

        <Input
          label="Label"
          placeholder="Optional, e.g. valve handwheel"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={isCreating}
        />

        <div>
          <label className="mb-1.5 block text-sm font-medium text-surface-200">Color</label>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            disabled={isCreating}
            className="h-10 w-20 rounded-lg border border-surface-700 bg-surface-900"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={handleClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} isLoading={isCreating} disabled={!assetId || !worldPoint}>
            Save Tag
          </Button>
        </div>
      </div>
    </Modal>
  );
}
